import { useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useIsMobile } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { DEMO_BEFORE_CLASS } from '../../constants';
import { useModeData } from '../../data/use-mode-data';
import { useA11yState } from '../../hooks/use-a11y-state';
import { useDemoContentLayout } from '../../hooks/use-demo-content-layout';
import { useDemoToggle } from '../../hooks/use-demo-toggle';
import { PANEL_OFFSET } from '../window';
import { ToggleButtons } from './toggle-buttons';

type DemoContentProps = {
  windowOpen: boolean;
};

const MOBILE_PREVIEW_MIN_MARGIN_PX = 20;
const TITLE_WAVE_GRADIENT_SIZE = 200;

export function DemoContent({ windowOpen }: DemoContentProps) {
  const { editMode, position } = useSettings();
  const { a11yState, modifiedSettingsCount } = useA11yState();
  const { toggleState, setToggleState } = useDemoToggle();
  const modeData = useModeData();
  const isMobile = useIsMobile();

  const isBefore = toggleState === 'before';

  const hasActiveMode = Boolean(a11yState.mode);
  const hasActiveSettings = modifiedSettingsCount > 0;

  const title = hasActiveMode
    ? `${modeData[a11yState.mode!].label} Mode Demonstration`
    : 'Custom Settings Demonstration';

  const isLeftPosition = position.position === 'left';

  const { desktopOpenTransformX, mobileOpenTransformY, desktopMaxWidth } =
    useDemoContentLayout({
      windowOpen,
      isLeftPosition,
      panelOffset: PANEL_OFFSET
    });

  const showDemo = editMode && (hasActiveMode || hasActiveSettings);

  // reset toggle state to before when demo is not shown
  useEffect(() => {
    if (!showDemo) {
      setToggleState('before');
    }
  }, [showDemo, setToggleState]);

  return (
    showDemo && (
      <Container
        className={isBefore ? DEMO_BEFORE_CLASS : ''}
        $windowOpen={windowOpen}
        $isMobile={isMobile}
        $desktopOpenTransformX={desktopOpenTransformX}
        $mobileOpenTransformY={mobileOpenTransformY}
        $desktopMaxWidth={desktopMaxWidth}
      >
        <ToggleButtons colorsContrast={a11yState.colorsContrast} />
        <Content data-eapps-accessibility-demo-container>
          <Title>{title}</Title>
          <Paragraph>
            This demo shows how the Elfsight Accessibility Widget works. First,
            you see the original page, then the selected mode is applied. Use
            the Before and After buttons to compare.
          </Paragraph>
          <Link href="#" onClick={(event) => event.preventDefault()}>
            Learn more about accessibility
          </Link>
        </Content>
      </Container>
    )
  );
}

const Container = styled.div<{
  $windowOpen: boolean;
  $isMobile: boolean;
  $desktopOpenTransformX: string;
  $mobileOpenTransformY: string;
  $desktopMaxWidth: string;
}>`
  ${({
    $windowOpen,
    $isMobile,
    $desktopOpenTransformX,
    $mobileOpenTransformY,
    $desktopMaxWidth
  }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 40px;
    width: min(100%, ${$desktopMaxWidth});
    max-width: fit-content;
    word-break: break-word;
    transition: transform 300ms;

    ${!$isMobile &&
    $windowOpen &&
    css`
      transform: translate(${$desktopOpenTransformX});
    `}

    ${$isMobile &&
    css`
      padding-top: 20px;
      width: calc(100% - ${MOBILE_PREVIEW_MIN_MARGIN_PX * 2}px);
      max-width: calc(100% - ${MOBILE_PREVIEW_MIN_MARGIN_PX * 2}px);

      ${$windowOpen &&
      css`
        transform: translateY(${$mobileOpenTransformY});
      `}
    `}
  `}
`;

const Content = styled.div`
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  max-width: 360px;
  background: #fafafa;
  color: #111;
  border-radius: 20px;
  padding: 24px;
  text-align: center;
`;

const titleWaveAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: -100% 50%;
  }
`;

const Title = styled.h2`
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  margin: 0 0 8px;
  background: linear-gradient(
      90deg,
      #ff5f6d 0%,
      #ffc371 10%,
      #4facfe 20%,
      #8b5cf6 30%,
      #43e97b 40%,
      #ff5f6d 50%,
      #ffc371 60%,
      #4facfe 70%,
      #8b5cf6 80%,
      #43e97b 90%,
      #ff5f6d 100%
    )
    0 50% / ${TITLE_WAVE_GRADIENT_SIZE}% 100% transparent;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${titleWaveAnimation} 5s linear infinite;
`;

const Paragraph = styled.p`
  font-size: 14px;
  line-height: 18px;
  margin: 0 0 20px;
`;

const Link = styled.a`
  color: currentColor;
  font-size: 12px;
  line-height: 14px;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
